import { triggerFloatingLink } from '@platejs/link/react';
import { Attachment01 } from '@untitledui/icons';
import { useEditorRef } from 'platejs/react';
import type { ReactNode } from 'react';
import React, { useCallback } from 'react';

interface EmailEditorToolbarProps {
  orgId?: number;
  onImageButtonClick?: () => void;
  onAttachFile?: (files: File[]) => void;
  isAttaching?: boolean;
  onAttachFileButtonClick?: () => void;
  /** Optional content rendered right-aligned at the end of the toolbar. */
  trailingContent?: ReactNode;
}

export const EmailEditorToolbar: React.FC<EmailEditorToolbarProps> = ({
  orgId,
  onImageButtonClick,
  onAttachFile,
  isAttaching,
  onAttachFileButtonClick,
  trailingContent,
}) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- plugin-specific tf methods (bold, italic, etc.) exist at runtime via parent Plate plugins
  const editor = useEditorRef<any>();

  const handleBold = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      editor.tf.bold.toggle();
    },
    [editor]
  );
  const handleItalic = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      editor.tf.italic.toggle();
    },
    [editor]
  );
  const handleUnderline = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      editor.tf.underline.toggle();
    },
    [editor]
  );
  // Link toolbar button needs two handlers to match Plate's canonical pattern
  // (@platejs/link/react's useLinkToolbarButton):
  //   - onMouseDown: only preventDefault() to stop focus shifting away from the editor
  //   - onClick:     refocus the editor then call triggerFloatingLink
  // Doing the trigger on mousedown (as this component previously did) races with
  // Slate's selection lifecycle — the Plate insert popup never opens because
  // internal guards evaluate against a stale/empty selection.
  const handleLinkMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
  }, []);
  const handleLink = useCallback(() => {
    editor.tf.focus();
    triggerFloatingLink(editor, { focused: true });
  }, [editor]);
  const handleUl = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      editor.tf.ul.toggle();
    },
    [editor]
  );
  const handleOl = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      editor.tf.ol.toggle();
    },
    [editor]
  );
  const handleH1 = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      editor.tf.h1.toggle();
    },
    [editor]
  );
  const handleH2 = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      editor.tf.h2.toggle();
    },
    [editor]
  );
  const handleH3 = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      editor.tf.h3.toggle();
    },
    [editor]
  );
  const handleBlockquote = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      editor.tf.blockquote.toggle();
    },
    [editor]
  );

  return (
    <div className='sticky top-0 left-0 right-0 z-10 flex items-center gap-0.5 px-3 py-0.5 border-b border-primary bg-white dark:bg-bg-secondary max-h-12 opacity-100 overflow-x-auto'>
      <div className='flex items-center'>
        <button
          type='button'
          className='p-2 hover:bg-primary hover:shadow-sm rounded-md text-sm font-medium text-secondary hover:text-primary transition-all duration-150 border border-transparent hover:border-primary'
          onMouseDown={handleBold}
          title='Bold (⌘+B)'
          aria-label='Bold'
        >
          <strong className='text-sm'>B</strong>
        </button>
        <button
          type='button'
          className='p-2 hover:bg-primary hover:shadow-sm rounded-md text-sm font-medium text-secondary hover:text-primary transition-all duration-150 border border-transparent hover:border-primary'
          onMouseDown={handleItalic}
          title='Italic (⌘+I)'
          aria-label='Italic'
        >
          <em className='text-sm not-italic'>I</em>
        </button>
        <button
          type='button'
          className='p-2 hover:bg-primary hover:shadow-sm rounded-md text-sm font-medium text-secondary hover:text-primary transition-all duration-150 border border-transparent hover:border-primary'
          onMouseDown={handleUnderline}
          title='Underline (⌘+U)'
          aria-label='Underline'
        >
          <span className='text-sm underline'>U</span>
        </button>
        <button
          type='button'
          className='p-2 hover:bg-primary hover:shadow-sm rounded-md text-sm font-medium text-secondary hover:text-primary transition-all duration-150 border border-transparent hover:border-primary'
          onMouseDown={handleLinkMouseDown}
          onClick={handleLink}
          title='Insert link (⌘+K)'
          aria-label='Insert link'
        >
          <svg
            className='w-4 h-4'
            fill='none'
            stroke='currentColor'
            viewBox='0 0 24 24'
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth={2}
              d='M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1'
            />
          </svg>
        </button>
        {orgId && (
          <button
            type='button'
            className='p-2 hover:bg-primary hover:shadow-sm rounded-md text-sm font-medium text-secondary hover:text-primary transition-all duration-150 border border-transparent hover:border-primary'
            onClick={onImageButtonClick}
            title='Insert image'
            aria-label='Insert image'
          >
            {/* Lucide Image icon */}
            <svg
              className='w-4 h-4'
              fill='none'
              stroke='currentColor'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
              viewBox='0 0 24 24'
            >
              <rect x='3' y='3' width='18' height='18' rx='2' ry='2' />
              <circle cx='8.5' cy='8.5' r='1.5' />
              <polyline points='21 15 16 10 5 21' />
            </svg>
          </button>
        )}
        {/* Attach File Button - after Image, before Lists */}
        {onAttachFile && (
          <button
            type='button'
            className='p-2 hover:bg-primary hover:shadow-sm rounded-md text-sm font-medium text-secondary hover:text-primary transition-all duration-150 border border-transparent hover:border-primary disabled:opacity-50 disabled:cursor-not-allowed'
            onClick={onAttachFileButtonClick}
            title={
              isAttaching ? 'Uploading…' : 'Attach file (supports multiple)'
            }
            aria-label='Attach file'
            disabled={isAttaching}
          >
            <Attachment01 className='w-4 h-4' />
          </button>
        )}
      </div>

      <div className='w-px h-5 bg-border-primary mx-2' />

      <div className='flex items-center'>
        <button
          type='button'
          className='p-2 hover:bg-primary hover:shadow-sm rounded-md text-sm font-medium text-secondary hover:text-primary transition-all duration-150 border border-transparent hover:border-primary'
          onMouseDown={handleUl}
          title='Bullet list'
          aria-label='Bullet list'
        >
          {/* Lucide List icon */}
          <svg
            className='w-4 h-4'
            fill='none'
            stroke='currentColor'
            strokeWidth='2'
            strokeLinecap='round'
            strokeLinejoin='round'
            viewBox='0 0 24 24'
          >
            <line x1='8' y1='6' x2='21' y2='6' />
            <line x1='8' y1='12' x2='21' y2='12' />
            <line x1='8' y1='18' x2='21' y2='18' />
            <line x1='3' y1='6' x2='3.01' y2='6' />
            <line x1='3' y1='12' x2='3.01' y2='12' />
            <line x1='3' y1='18' x2='3.01' y2='18' />
          </svg>
        </button>
        <button
          type='button'
          className='p-2 hover:bg-primary hover:shadow-sm rounded-md text-sm font-medium text-secondary hover:text-primary transition-all duration-150 border border-transparent hover:border-primary'
          onMouseDown={handleOl}
          title='Numbered list'
          aria-label='Numbered list'
        >
          {/* Lucide ListOrdered icon */}
          <svg
            className='w-4 h-4'
            fill='none'
            stroke='currentColor'
            strokeWidth='2'
            strokeLinecap='round'
            strokeLinejoin='round'
            viewBox='0 0 24 24'
          >
            <line x1='10' y1='6' x2='21' y2='6' />
            <line x1='10' y1='12' x2='21' y2='12' />
            <line x1='10' y1='18' x2='21' y2='18' />
            <path d='M4 6h1v4' />
            <path d='M4 10h2' />
            <path d='M6 18H4c0-1 2-2 2-3s-1-1.5-2-1' />
          </svg>
        </button>
      </div>

      <div className='w-px h-5 bg-border-primary mx-2' />

      <div className='flex items-center'>
        <button
          type='button'
          className='px-2.5 py-2 hover:bg-primary hover:shadow-sm rounded-md text-xs font-medium text-secondary hover:text-primary transition-all duration-150 border border-transparent hover:border-primary'
          onMouseDown={handleH1}
          title='Heading 1'
          aria-label='Heading 1'
        >
          H1
        </button>
        <button
          type='button'
          className='px-2.5 py-2 hover:bg-primary hover:shadow-sm rounded-md text-xs font-medium text-secondary hover:text-primary transition-all duration-150 border border-transparent hover:border-primary'
          onMouseDown={handleH2}
          title='Heading 2'
          aria-label='Heading 2'
        >
          H2
        </button>
        <button
          type='button'
          className='px-2.5 py-2 hover:bg-primary hover:shadow-sm rounded-md text-xs font-medium text-secondary hover:text-primary transition-all duration-150 border border-transparent hover:border-primary'
          onMouseDown={handleH3}
          title='Heading 3'
          aria-label='Heading 3'
        >
          H3
        </button>
        <button
          type='button'
          className='p-2 hover:bg-primary hover:shadow-sm rounded-md text-sm font-medium text-secondary hover:text-primary transition-all duration-150 border border-transparent hover:border-primary'
          onMouseDown={handleBlockquote}
          title='Quote'
          aria-label='Quote'
        >
          <span className='text-lg leading-none'>"</span>
        </button>
      </div>

      {trailingContent && (
        <div className='ml-auto flex items-center pl-2'>{trailingContent}</div>
      )}
    </div>
  );
};
